// --- START FILE: Zod validators for catalog API responses ---
//
// The catalog API contract for money/percent fields:
//   - gross_price and discount_percent are returned as strings with 2 decimal
//     places (e.g. "15.00", "0.00") from the backend (Postgres NUMERIC/Decimal
//     serialized via Pydantic field_serializer).
//   - These validators accept both string and number (defensive, forward-compat)
//     and normalize to a JS number for internal UI computations.
//
// See docs/api-contract-catalog.md for the full API contract.
// --- END FILE header ---

import { z } from "zod";

// ─── Shared coercion helper ───────────────────────────────────────────────────

/**
 * Accept a money/percent field as either a string like "15.00" or a number.
 * Normalizes to a JS number for UI math (e.g. formatCurrency, arithmetic).
 * Returns NaN for values that can't be coerced — callers should guard with
 * Number.isFinite() when needed.
 */
const numericStringOrNumber = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === "string" ? parseFloat(v) : v));

// ─── ProductCategory ─────────────────────────────────────────────────────────

export const productCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable().optional(),
  sort_order: z.number(),
  is_active: z.boolean(),
});

// ─── ProductImage ─────────────────────────────────────────────────────────────

export const productImageSchema = z.object({
  id: z.number(),
  r2_key: z.string(),
  url: z.string(),
  alt: z.string().nullable().optional(),
  caption: z.string().nullable().optional(),
  sort_order: z.number(),
});

// ─── Product ─────────────────────────────────────────────────────────────────

export const productSchema = z.object({
  id: z.number(),
  product_code: z.number(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable().optional(),
  // gross_price: backend returns a 2dp string; normalize to number for UI use.
  gross_price: numericStringOrNumber,
  is_active: z.boolean(),
  is_archived: z.boolean(),
  stl_reference: z.string().nullable().optional(),
  categories: z.array(productCategorySchema).default([]),
  images: z.array(productImageSchema).default([]),
  created_at: z.string(),
  updated_at: z.string(),
});

export const productArraySchema = z.array(productSchema);

export type ParsedProduct = z.infer<typeof productSchema>;
export type ParsedProductCategory = z.infer<typeof productCategorySchema>;

// ─── RetailFilamentBrand ──────────────────────────────────────────────────────

export const retailFilamentBrandSchema = z.object({
  id: z.number(),
  name: z.string(),
  slug: z.string(),
  logo_url: z.string().nullable().optional(),
  banner_url: z.string().nullable().optional(),
  banner_r2_key: z.string().nullable().optional(),
  sort_order: z.number(),
  is_active: z.boolean(),
});

// ─── RetailFilamentMaterial ───────────────────────────────────────────────────

export const retailFilamentMaterialSchema = z.object({
  id: z.number(),
  brand_id: z.number().nullable().optional(), // null after globalisation migration
  name: z.string(),
  slug: z.string(),
  sort_order: z.number(),
  is_active: z.boolean(),
});

// ─── RetailFilamentImage ──────────────────────────────────────────────────────

export const retailFilamentImageSchema = z.object({
  id: z.number(),
  r2_key: z.string(),
  url: z.string(),
  alt: z.string().nullable().optional(),
  caption: z.string().nullable().optional(),
  sort_order: z.number(),
});

// ─── RetailFilamentRoll ───────────────────────────────────────────────────────

export const retailFilamentRollSchema = z.object({
  id: z.number(),
  brand_id: z.number(),
  material_id: z.number(),
  name: z.string().nullable().optional(),
  slug: z.string(),
  description: z.string().nullable().optional(),
  color_name: z.string(),
  color_hex: z.string(),
  weight_grams: z.number(),
  // gross_price / discount_percent: backend returns 2dp strings; normalize to numbers.
  gross_price: numericStringOrNumber,
  discount_percent: numericStringOrNumber,
  is_active: z.boolean(),
  is_archived: z.boolean(),
  brand: retailFilamentBrandSchema.nullable().optional(),
  material: retailFilamentMaterialSchema.nullable().optional(),
  images: z.array(retailFilamentImageSchema).default([]),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export const retailFilamentRollArraySchema = z.array(retailFilamentRollSchema);

export type ParsedRetailFilamentRoll = z.infer<typeof retailFilamentRollSchema>;
export type ParsedRetailFilamentBrand = z.infer<typeof retailFilamentBrandSchema>;
export type ParsedRetailFilamentMaterial = z.infer<typeof retailFilamentMaterialSchema>;

/**
 * Safely parse an array of products; logs validation errors and returns an
 * empty array on failure so the UI shows a meaningful empty state instead of
 * crashing.
 */
export function safeParseProducts(data: unknown): ParsedProduct[] {
  const result = productArraySchema.safeParse(data);
  if (!result.success) {
    console.error("[catalog] Product list validation failed:", result.error.flatten());
    return [];
  }
  return result.data;
}

/**
 * Safely parse a single product; throws on failure so the caller can show an
 * error state.
 */
export function parseProduct(data: unknown): ParsedProduct {
  return productSchema.parse(data);
}

/**
 * Safely parse an array of filament rolls; logs and returns [] on failure.
 */
export function safeParseFilamentRolls(data: unknown): ParsedRetailFilamentRoll[] {
  const result = retailFilamentRollArraySchema.safeParse(data);
  if (!result.success) {
    console.error("[catalog] Filament roll list validation failed:", result.error.flatten());
    return [];
  }
  return result.data;
}

/**
 * Safely parse a single filament roll; throws on failure.
 */
export function parseFilamentRoll(data: unknown): ParsedRetailFilamentRoll {
  return retailFilamentRollSchema.parse(data);
}
// --- END FILE: Zod validators for catalog API responses ---
